import { useState, useEffect } from 'react'
import { Container, Form, Button, Card, Badge, Alert, Spinner, Modal } from 'react-bootstrap'
import { useProfile } from '../contexts/ProfileContext'
import profileService from '../services/profileService'
import api from '../services/api'

function Curate() {
  const { activeProfile } = useProfile()
  const [thing, setThing] = useState('')
  const [thingType, setThingType] = useState('website')
  const [score, setScore] = useState(75)
  const [minAge, setMinAge] = useState('')
  const [maxAge, setMaxAge] = useState('')
  const [keywords, setKeywords] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [myScores, setMyScores] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [scoreToDelete, setScoreToDelete] = useState(null)

  useEffect(() => {
    if (activeProfile) {
      loadMyScores()
    }
  }, [activeProfile])

  const loadMyScores = async () => {
    if (!activeProfile) return

    try {
      const scores = await profileService.getScores(activeProfile.id)
      setMyScores(scores)
    } catch (err) {
      console.error('Failed to load scores:', err)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!activeProfile) {
      setError('Please select a profile first')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const scoreData = {
        thing: thing.trim(),
        thing_type: thingType,
        score: parseInt(score),
        min_age: minAge ? parseInt(minAge) : null,
        max_age: maxAge ? parseInt(maxAge) : null,
        keywords: keywords ? keywords.split(',').map(k => k.trim()) : [],
        pending: false
      }

      await api.post(`/profiles/${activeProfile.id}/scores`, scoreData)

      setSuccess('Content scored successfully!')
      setThing('')
      setKeywords('')
      setMinAge('')
      setMaxAge('')
      setScore(75)
      loadMyScores()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to score content')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!scoreToDelete) return

    try {
      await profileService.deleteScore(activeProfile.id, scoreToDelete)
      setSuccess('Score deleted successfully')
      loadMyScores()
    } catch (err) {
      setError('Failed to delete score')
    } finally {
      setShowDeleteModal(false)
      setScoreToDelete(null)
    }
  }

  const openDeleteModal = (scoreId) => {
    setScoreToDelete(scoreId)
    setShowDeleteModal(true)
  }

  return (
    <Container className="mt-4">
      <h2>Curate Content</h2>
      {activeProfile && (
        <p className="text-muted">
          Curating as: <strong>{activeProfile.name}</strong>
        </p>
      )}

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Add New Score</Card.Title>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>URL or Identifier *</Form.Label>
              <Form.Control
                type="text"
                placeholder="https://example.com or identifier"
                value={thing}
                onChange={(e) => setThing(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type *</Form.Label>
              <Form.Select
                value={thingType}
                onChange={(e) => setThingType(e.target.value)}
              >
                <option value="website">Website</option>
                <option value="app">App</option>
                <option value="person">Person</option>
                <option value="service">Service</option>
                <option value="recipe">Recipe</option>
                <option value="geoplace">Place</option>
                <option value="physicalobject">Physical Object</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Score: {score}</Form.Label>
              <Form.Range
                min="0"
                max="100"
                value={score}
                onChange={(e) => setScore(e.target.value)}
              />
              <Form.Text className="text-muted">
                0 = Poor, 50 = Average, 100 = Excellent
              </Form.Text>
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Age (optional)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 13"
                    value={minAge}
                    onChange={(e) => setMinAge(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Maximum Age (optional)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 18"
                    value={maxAge}
                    onChange={(e) => setMaxAge(e.target.value)}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Keywords (comma-separated)</Form.Label>
              <Form.Control
                type="text"
                placeholder="science, education, research"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
              />
            </Form.Group>

            <Button type="submit" variant="primary" disabled={loading || !activeProfile}>
              {loading ? <><Spinner size="sm" className="me-2" /> Adding...</> : 'Add Score'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <h3>My Scores</h3>
      {myScores.length === 0 ? (
        <p className="text-muted">No scores yet. Add your first one above!</p>
      ) : (
        myScores.map((scoreItem) => (
          <Card key={scoreItem.id} className="mb-3">
            <Card.Body>
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2">
                <div className="flex-grow-1">
                  <Card.Title className="d-flex flex-wrap align-items-center gap-1">
                    <span>{scoreItem.scorable?.pretty_name || scoreItem.scorable?.thing}</span>
                    <Badge bg="success">
                      {scoreItem.score}
                    </Badge>
                    {scoreItem.pending && (
                      <Badge bg="warning">Pending</Badge>
                    )}
                  </Card.Title>
                  <Card.Text>
                    <strong>Type:</strong> {scoreItem.scorable?.thing_type}
                  </Card.Text>
                  {scoreItem.scorable?.summary && (
                    <Card.Text>{scoreItem.scorable.summary}</Card.Text>
                  )}
                  <Card.Link
                    href={scoreItem.scorable?.thing}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="d-inline-block text-break"
                    style={{ maxWidth: '100%' }}
                  >
                    {scoreItem.scorable?.thing}
                  </Card.Link>
                  {(scoreItem.min_age || scoreItem.max_age) && (
                    <div className="mt-2">
                      <small className="text-muted">
                        Ages: {scoreItem.min_age || '0'} - {scoreItem.max_age || '∞'}
                      </small>
                    </div>
                  )}
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="flex-shrink-0 mt-2 mt-sm-0"
                  onClick={() => openDeleteModal(scoreItem.id)}
                >
                  Delete
                </Button>
              </div>
            </Card.Body>
          </Card>
        ))
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Score</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this score?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default Curate
